import { getSession, signIn } from 'next-auth/react'

/**
 * Empty by default, which means every request is same-origin and is proxied to the
 * backend by the `/api/:path*` rewrite in `next.config.ts`.
 *
 * IT USED TO DEFAULT TO `http://localhost:8001`, and that made every console request
 * cross-origin. The Dashboard is what exposed the cost: the browser sent
 * `OPTIONS /api/scc/dashboard`, the server answered 200, the GET never followed, and the
 * screen rendered "Failed to fetch" against an endpoint that answered fine to curl. A
 * preflight is a second request that has to succeed on a second hostname before the
 * first one is allowed, and none of that buys anything when the console and its API are
 * served from one place.
 *
 * Setting `NEXT_PUBLIC_API_URL` still overrides it, because a deployment that serves the
 * API from a different host needs exactly that. Local development no longer does.
 */
const RAW_API_URL = process.env.NEXT_PUBLIC_API_URL || ''

/**
 * Ensure the API URL uses the same protocol as the current page.
 * Prevents Mixed Content errors when the page is served over HTTPS
 * but NEXT_PUBLIC_API_URL was accidentally configured with http://.
 *
 * A relative base has no protocol to correct, so it falls through untouched.
 */
export const API_URL = (() => {
  if (
    RAW_API_URL &&
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:'
  ) {
    return RAW_API_URL.replace(/^http:\/\//i, 'https://')
  }
  return RAW_API_URL
})()
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

/**
 * Error thrown by the API client that preserves the HTTP status code and the
 * raw `detail` payload from the backend. Endpoints such as the gate decision
 * return a 409 with a structured `detail` object (policy_block / out_of_sequence)
 * — callers can `instanceof ApiError` and inspect `.status` / `.detail`.
 */
export class ApiError<TDetail = unknown> extends Error {
  status: number
  detail: TDetail
  constructor(message: string, status: number, detail: TDetail) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/test' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 */
async function apiClientFetch<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const session = await getSession()

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // Handle session errors that occurred during a token refresh attempt.
  if (session?.error === 'RefreshAccessTokenError') {
    // Force sign-in to get a new, valid session.
    await signIn('keycloak')
    // Throw an error to stop the current, failing API call.
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL: http://localhost:8001/app1/api/test
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })

  // If the backend returns a 401, it means the token is invalid or expired.
  // This can happen if the backend is restarted or the user's session is revoked.
  // We trigger a re-login to fix the session.
  if (response.status === 401) {
    await signIn('keycloak')
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    const detail = (errorData as { detail?: unknown }).detail
    // `detail` may be a string (plain message) or an object (structured error,
    // e.g. the 409 policy_block / out_of_sequence payloads). Surface a readable
    // message but keep the full detail available on the thrown ApiError.
    const message =
      typeof detail === 'string'
        ? detail
        : (detail as { message?: string } | undefined)?.message || 'An API error occurred.'
    throw new ApiError(message, response.status, detail)
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

/**
 * A specialized fetch for binary data (Blobs).
 */
async function apiClientFetchBlob(endpoint: string, options: RequestInit = {}): Promise<Blob> {
  const session = await getSession()
  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  if (session?.error === 'RefreshAccessTokenError') {
    await signIn('keycloak')
    throw new Error('Session expired. Please sign in again.')
  }

  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`
  const response = await fetch(fullUrl, { ...options, headers })

  if (response.status === 401) {
    await signIn('keycloak')
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch document: ${response.statusText}`)
  }

  return response.blob()
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a GET request that returns a Blob.
   */
  getBlob: (endpoint: string, options?: RequestInit): Promise<Blob> => {
    return apiClientFetchBlob(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a file upload (multipart/form-data POST).
   * NOTE: Do NOT set Content-Type — the browser sets it with the correct boundary.
   */
  upload: <T = unknown>(endpoint: string, formData: FormData, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },
}

// Default export for backward compatibility
export default apiClientFetch

// ---------------------------------------------------------------------------
// Public Sector Tender API helper
// All endpoints are under /api/tenders/ (Phase 4 router from implementation_plan.md)
// ---------------------------------------------------------------------------

import type {
  TenderActivity,
  PipelineItem,
  BusinessCase,
  Bid,
  EvaluationResult,
  PerformanceReport,
  AuditEntry,
  GateDecisionRequest,
  GateState,
} from '@/types/tender'

interface PipelineFilters {
  business_unit?: string
  stage?: number
  value_min?: number
  value_max?: number
  category?: string
}

interface PipelineResponse {
  items: PipelineItem[]
  summary: {
    total: number
    by_stage: Record<number, number>
    total_estimated_value: number
    pending_gates: number
  }
}

interface CreateTenderPayload {
  title: string
  authority: string
  business_unit: string
  category: string
  estimated_value: number
  currency?: string
  description?: string
  framework_available?: boolean
  urgency?: string
}

interface GenerateBusinessCasePayload {
  requirement: string
  estimated_value: number
  stakeholder_name?: string
}

interface TenderChatPayload {
  messages: { role: string; content: string }[]
  tender_ref?: string
  current_stage?: number
  page_context?: string
}

interface TenderConfig {
  default_currency: string
  supported_currencies: string[]
  gate_auto_approve: boolean
}

// ---- Phase 2 workbench orchestration -----------------------------------------

/** The set of AI actions that can be driven from the workbench. */
export type TenderActionKey =
  | 'generate_business_case'
  | 'publish_notice'
  | 'draft_clarifications'
  | 'evaluate_bids'
  | 'generate_award'
  | 'publish_award_notice'
  | 'assess_performance'

/** GET /api/tenders/{ref}/next-action — what should happen next. */
export type TenderNextAction =
  | {
      actionable: true
      action: TenderActionKey
      label: string
      current_stage: number
    }
  | {
      actionable: false
      pending_gate: string
      step_label: string
      role: string
      message: string
    }
  | {
      actionable: false
      message: string
    }

/** POST /api/tenders/{ref}/actions/{action} — result of running an AI step. */
export interface TenderActionResult {
  ok: boolean
  action: string
  label: string
  current_stage: number
  active_operator: string | null
  pending_gate: string | null
  blocks: string[]
  /**
   * Phase 3 policy-engine summary for the step that just ran, if any policies
   * were evaluated. `null` when the action produced no verdicts.
   */
  verdicts: { pass: number; flag: number; block: number } | null
}

// ---- Phase 3 policy-engine verdicts -----------------------------------------

/** A single policy-check verdict (Phase 3 policy engine). */
export interface PolicyCheckVerdict {
  policy_id: string
  policy_name: string
  result: 'PASS' | 'FLAG' | 'BLOCK'
  rationale: string
  check_type: 'rule' | 'llm' | 'hybrid' | 'manual'
  source_document?: string | null
  source_clause?: string | null
  is_bid_level: boolean
}

/** The verdicts for a single gate, with a roll-up summary. */
export interface GateVerdicts {
  gate: string
  step: string
  summary: { pass: number; flag: number; block: number }
  checks: PolicyCheckVerdict[]
}

/** GET /api/tenders/{ref}/policy-verdicts — verdicts grouped by gate (G1..G5). */
export interface PolicyVerdictsResponse {
  gates: Record<string, GateVerdicts>
}

/** A single entry from GET /api/tenders/{ref}/activity. */
export interface TenderActivityEntry {
  id: number | string
  action: string
  tool_called: string | null
  operator_id: string | null
  stage: number | null
  gate_id: string | null
  timestamp: string | null
  is_human: boolean
  human_decision?: {
    decision?: 'approved' | 'returned'
    approver?: string
    reason?: string
    rationale?: string
    decided_at?: string
  } | null
}

export interface TenderActivityFeed {
  activity: TenderActivityEntry[]
  total: number
}

export interface TenderClarificationItem {
  id: number
  clarification_ref: string | null
  question: string
  asked_by: string | null
  asked_at: string | null
  draft_answer: string | null
  status: 'pending' | 'drafted' | 'approved' | 'issued'
  is_shared_to_all: boolean
  answered_at: string | null
  issued_at: string | null
}

export interface TenderClarificationLog {
  tender_ref: string
  clarifications: TenderClarificationItem[]
  count: number
}

export interface TenderContractItem {
  id: number
  contract_number: string
  title: string
  supplier: string
  value: number | null
  currency: string
  status: string | null
  start_date: string | null
  end_date: string | null
  template_type: string | null
  kpi_health: 'on_track' | 'breach' | 'none'
  source_tender_ref: string | null
  expiring_soon: boolean
}

export interface TenderContractRegistry {
  kpis: { active: number; total_value: number; expiring_90d: number; open_breaches: number }
  contracts: TenderContractItem[]
  total: number
}

export interface TenderContractDetail extends TenderContractItem {
  award_report_ref: string | null
  contract_pack: Record<string, unknown> | null
  kpi_records: {
    kpi_name: string; target: number; actual: number; unit: string | null
    is_breached: boolean; breach_severity: string | null; service_credit_amount: number
  }[]
}

/** Structured 409 detail returned by POST /gate/{gate_id}. */
export interface PolicyBlockEntry {
  policy_id: string
  policy_name: string
  rationale: string
  source: string
}
export type GateDecisionConflictDetail =
  | { error: 'policy_block'; message: string; blocks: PolicyBlockEntry[] }
  | { error: 'out_of_sequence'; message: string }

/** Extended gate-decision body — Phase 2 adds the `override` flag. */
export interface GateDecisionBody {
  decision: 'approved' | 'returned'
  reason: string
  approver: string
  override?: boolean
  /**
   * Phase 3 — policy ids of FLAG verdicts the approver explicitly acknowledged.
   * The backend safely ignores unknown fields, so this is additive.
   */
  acknowledged_flags?: string[]
}

/** Success payload from POST /gate/{gate_id}. */
export interface GateDecisionResult {
  gate: string
  current_stage: number
  active_operator: string | null
}

export interface TenderDashboardData {
  currency: string
  kpi_cards: {
    active_tenders: number
    active_tenders_trend: string
    pipeline_value: number
    pipeline_value_trend: string
    gates_pending: number
    gates_pending_change: string
    policy_flags_open: number
    policy_flags_change: string
  }
  pipeline_overview: {
    by_stage: Array<{ stage: number; label: string; count: number; value: number }>
    by_route: Array<{ key: string; count: number; value: number }>
    by_category: Array<{ key: string; count: number; value: number }>
  }
  value_intelligence: Array<{
    authority: string
    tender_count: number
    estimated_value: number
    committed_value: number
    pending_gates: number
    status: 'on_track' | 'attention'
  }>
  key_metrics: {
    gate_pass_rate: number
    on_time_award: number
    avg_quality: number
  }
  policy_compliance: Array<{
    policy_id: string
    policy_name: string
    result: 'PASS' | 'FLAG' | 'BLOCK'
    tender_ref: string
    checked_at: string
  }>
  recent_activity: Array<{
    action: string
    operator_id: string
    stage: number
    tender_ref: string
    tool_called: string
    timestamp: string
  }>
  my_approvals: Array<{
    tender_ref: string
    tender_title: string
    authority: string
    estimated_value: number
    currency: string
    step: string
    role: string
  }>
  my_tenders: Array<{
    tender_ref: string
    title: string
    stage: number
    stage_label: string
    authority: string
    estimated_value: number
    currency: string
  }>
  aggregation_suggestions: Array<{
    keyword: string
    category: string
    count: number
    authorities: string[]
    total_value: number
  }>
}

export const tenderApi = {
  /** GET /api/tenders/config — currency default, gate auto-approve flag */
  getConfig: (): Promise<TenderConfig> =>
    apiClient.get('/api/tenders/config'),

  /** GET /api/tenders/dashboard — PCC-rich command-center summary */
  getDashboard: (): Promise<TenderDashboardData> =>
    apiClient.get('/api/tenders/dashboard'),

  /** GET /api/tenders/pipeline — all tenders grouped by stage */
  getPipeline: (filters?: PipelineFilters): Promise<PipelineResponse> => {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.set(key, String(value))
      })
    }
    const query = params.toString()
    return apiClient.get(`/api/tenders/pipeline${query ? `?${query}` : ''}`)
  },

  /** POST /api/tenders/ — create a new TenderActivity */
  createTender: (data: CreateTenderPayload): Promise<TenderActivity> =>
    apiClient.post('/api/tenders/', data),

  /** POST /api/tenders/{ref}/business-case — generate HMT Five Case (O2 + O3) */
  generateBusinessCase: (ref: string, data: GenerateBusinessCasePayload): Promise<BusinessCase> =>
    apiClient.post(`/api/tenders/${ref}/business-case`, data),

  /** GET /api/tenders/{ref}/business-case — fetch existing business case */
  getBusinessCase: (ref: string): Promise<BusinessCase> =>
    apiClient.get(`/api/tenders/${ref}/business-case`),

  /** POST /api/tenders/{ref}/route — route recommendation (O3) */
  triggerRouteSelection: (ref: string): Promise<{ route: string; rationale: string }> =>
    apiClient.post(`/api/tenders/${ref}/route`),

  /** POST /api/tenders/{ref}/notice — market notice generation (O4) */
  generateNotice: (ref: string, noticeType: string): Promise<{ content: string }> =>
    apiClient.post(`/api/tenders/${ref}/notice`, { notice_type: noticeType }),

  /** GET /api/tenders/{ref} — full tender detail including bids[] and winning_vendor_id */
  getTender: (ref: string): Promise<TenderActivity> =>
    apiClient.get(`/api/tenders/${ref}`),

  /** GET /api/tenders/{ref}/bids — fetch ingested bids */
  getBids: (ref: string): Promise<Bid[]> =>
    apiClient.get(`/api/tenders/${ref}/bids`),

  /** POST /api/tenders/{ref}/generate-bids — AI-generate contextual bidders for Bidders Overview */
  generateBids: (ref: string): Promise<{ tender_ref: string; bids_generated: number; bidder_names: string[]; sector_rationale: string; skipped: boolean }> =>
    apiClient.post(`/api/tenders/${ref}/generate-bids`),

  /** POST /api/tenders/{ref}/evaluate — full evaluation pipeline (O6 + O7) */
  triggerEvaluation: (ref: string): Promise<EvaluationResult> =>
    apiClient.post(`/api/tenders/${ref}/evaluate`),

  /** GET /api/tenders/{ref}/evaluation — fetch existing evaluation result */
  getEvaluation: (ref: string): Promise<EvaluationResult> =>
    apiClient.get(`/api/tenders/${ref}/evaluation`),

  /** POST /api/tenders/{ref}/award — contract pack + award report (O8) */
  triggerAward: (ref: string): Promise<{ contract_id: number; award_report_url: string }> =>
    apiClient.post(`/api/tenders/${ref}/award`),

  /** GET /api/tenders/{ref}/performance — KPI records + service credits */
  getPerformance: (ref: string): Promise<PerformanceReport> =>
    apiClient.get(`/api/tenders/${ref}/performance`),

  /** GET /api/tenders/{ref}/audit-pack — full audit trail for evidence export */
  getAuditPack: (ref: string): Promise<AuditEntry[]> =>
    apiClient.get(`/api/tenders/${ref}/audit-pack`),

  /**
   * POST /api/tenders/{ref}/gate/{gate_id} — approve or return a gate.
   * Enforces Keycloak role on backend; returns 403 if user lacks required role.
   */
  submitGateDecision: (ref: string, gateId: string, payload: GateDecisionRequest): Promise<GateState> =>
    apiClient.post(`/api/tenders/${ref}/gate/${gateId}`, payload),

  /**
   * POST /api/tenders/{ref}/gate/{gate_id} — Phase 2 gate decision.
   * Accepts the `override` flag and returns {gate, current_stage, active_operator}.
   * On a policy block / out-of-sequence error the client throws an ApiError with
   * status 409 and `detail` = GateDecisionConflictDetail.
   */
  decideGate: (ref: string, gateId: string, body: GateDecisionBody): Promise<GateDecisionResult> =>
    apiClient.post(`/api/tenders/${ref}/gate/${gateId}`, body),

  /** GET /api/tenders/{ref}/next-action — actionable AI step or pending-gate info. */
  getNextAction: (ref: string): Promise<TenderNextAction> =>
    apiClient.get(`/api/tenders/${ref}/next-action`),

  /** POST /api/tenders/{ref}/actions/{action} — run an AI orchestration step. */
  runAction: (ref: string, action: TenderActionKey): Promise<TenderActionResult> =>
    apiClient.post(`/api/tenders/${ref}/actions/${action}`, {}),

  /** GET /api/tenders/{ref}/activity — AI + human activity feed (read-only). */
  getActivity: (ref: string): Promise<TenderActivityFeed> =>
    apiClient.get(`/api/tenders/${ref}/activity`),

  /** GET /api/tenders/{ref}/clarifications — clarification Q&A log. */
  getClarifications: (ref: string): Promise<TenderClarificationLog> =>
    apiClient.get(`/api/tenders/${ref}/clarifications`),

  /** GET /api/tenders/contracts — Contract Hub registry (tender-linked contracts + KPIs). */
  getTenderContracts: (): Promise<TenderContractRegistry> =>
    apiClient.get(`/api/tenders/contracts`),

  /** GET /api/tenders/contracts/{id} — read-only contract detail. */
  getTenderContractDetail: (id: number): Promise<TenderContractDetail> =>
    apiClient.get(`/api/tenders/contracts/${id}`),

  /** POST /api/tenders/{ref}/clarifications/{id} — save a human-edited answer before issuing. */
  saveClarification: (ref: string, id: number, draftAnswer: string): Promise<TenderClarificationItem> =>
    apiClient.post(`/api/tenders/${ref}/clarifications/${id}`, { draft_answer: draftAnswer }),

  /**
   * GET /api/tenders/{ref}/policy-verdicts — Phase 3 policy-engine verdicts
   * grouped by the five approval gates (G1..G5). Each gate carries a roll-up
   * summary and the underlying check list (rule / LLM / hybrid / manual).
   */
  getPolicyVerdicts: (ref: string): Promise<PolicyVerdictsResponse> =>
    apiClient.get(`/api/tenders/${ref}/policy-verdicts`),

  /** POST /api/tenders/chat — tender AI agent chat (TENDER_SYSTEM_INSTRUCTION) */
  chat: (payload: TenderChatPayload): Promise<{ response: string; tool_calls?: unknown[] }> =>
    apiClient.post('/api/tenders/chat', payload),

  /**
   * GET /api/tenders/{ref}/contract
   * Returns the contract linked to a tender (Plan 2 — CH-01, CH-05).
   */
  getContractForTender: (tenderRef: string): Promise<{ contract_id: number | null; contract_number: string | null; tender_ref: string }> =>
    apiClient.get(`/api/tenders/${tenderRef}/contract`),

  /**
   * POST /api/tenders/{ref}/select-mat — human override of the AI-recommended MAT.
   * Uses the standard authenticated apiClient (direct to the FastAPI backend with the
   * user's bearer token), the same path every other tender endpoint uses. This avoids
   * the server-side proxy, which broke inside Docker (the proxy could not resolve the
   * browser-facing localhost URL from within the frontend container).
   */
  selectAlternateMat: (ref: string, bidId: number, rationale: string): Promise<{
    ok: boolean; bid_id: number; bidder_name: string; previous_mat: string; rationale: string
  }> =>
    apiClient.post(`/api/tenders/${ref}/select-mat`, { bid_id: bidId, rationale }),

  /**
   * GET /api/tenders/bids?vendor_id={id}
   * Returns all bids submitted by a specific vendor (Plan 2 — VE-01).
   */
  getVendorBids: (vendorId: number): Promise<Bid[]> =>
    apiClient.get(`/api/tenders/bids?vendor_id=${vendorId}`),
}
